// src/app/features/markets/useMarketData.js
import { useQuery } from "@tanstack/react-query";

/**
 * FREE, REAL market data:
 * - Primary: BINANCE (no API key) for symbols that exist on Binance spot
 * - FX fallback: exchangerate.host (free, CORS-friendly) when Binance symbol is missing
 *
 * Supported patterns:
 * - Binance: BTCUSDT, ETHUSDT, SOLUSDT, PAXGUSDT, etc.
 * - FX fallback: "EURUSDT" -> uses exchangerate.host EUR->USD as approximation for EUR->USDT
 */

function normalizeSymbol(symbol) {
  return String(symbol || "")
    .replace("/", "")
    .replace("-", "")
    .trim()
    .toUpperCase();
}

function parseFxLikeSymbol(sym) {
  // Expect e.g. EURUSDT -> base=EUR quote=USDT
  const s = normalizeSymbol(sym);
  if (s.length !== 7) return null;
  const base = s.slice(0, 3);
  const quote = s.slice(3);
  if (!/^[A-Z]{3}$/.test(base)) return null;
  if (quote !== "USDT") return null;
  return { base, quote };
}

async function fetchBinanceKlines(symbol, interval, limit) {
  const url = `https://api.binance.com/api/v3/klines?symbol=${encodeURIComponent(
    symbol
  )}&interval=${encodeURIComponent(interval)}&limit=${Number(limit || 100)}`;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`Binance klines failed (${res.status})`);

  const rows = await res.json();
  return (Array.isArray(rows) ? rows : []).map((k) => ({
    time: Number(k[0]),
    open: Number(k[1]),
    high: Number(k[2]),
    low: Number(k[3]),
    close: Number(k[4]),
    volume: Number(k[5]),
  }));
}

async function fetchBinancePrice(symbol) {
  const url = `https://api.binance.com/api/v3/ticker/price?symbol=${encodeURIComponent(symbol)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Binance price failed (${res.status})`);
  const json = await res.json();
  return { symbol, price: Number(json.price) };
}

async function fetchBinance24h(symbol) {
  const url = `https://api.binance.com/api/v3/ticker/24hr?symbol=${encodeURIComponent(symbol)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Binance 24hr failed (${res.status})`);
  const json = await res.json();
  return {
    symbol,
    price: Number(json.lastPrice),
    changePercent: Number(json.priceChangePercent),
  };
}

/**
 * FX fallback: exchangerate.host (free)
 * We'll approximate BASE/USDT by BASE/USD (since USDT ~ USD).
 *
 * Example:
 *  EURUSDT -> convert?from=EUR&to=USD&amount=1
 */
async function fetchFxFallback(symbol) {
  const fx = parseFxLikeSymbol(symbol);
  if (!fx) throw new Error("FX fallback not applicable");

  const url = `https://api.exchangerate.host/convert?from=${encodeURIComponent(
    fx.base
  )}&to=USD&amount=1`;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`FX fallback failed (${res.status})`);

  const json = await res.json();
  const rate = Number(json?.result);

  if (!Number.isFinite(rate)) {
    throw new Error("FX fallback returned invalid rate");
  }

  return {
    symbol,
    price: rate,
    changePercent: 0, // not provided reliably by this endpoint
    source: "exchangerate.host",
  };
}

/**
 * Hook: useMarketData (single symbol)
 * - History/price are Binance-only (since FX fallback won't provide OHLC candles in this simple free setup)
 * - If you need FX candles too, we can add a second fallback provider for OHLC.
 */
export function useMarketData(backendSymbol, interval = "1h", limit = 100) {
  const symbol = normalizeSymbol(backendSymbol);

  const historyQuery = useQuery({
    queryKey: ["market-history", symbol, interval, limit],
    queryFn: () => fetchBinanceKlines(symbol, interval, limit),
    enabled: !!symbol,
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: 2,
  });

  const priceQuery = useQuery({
    queryKey: ["market-price", symbol],
    queryFn: () => fetchBinancePrice(symbol),
    enabled: !!symbol,
    refetchInterval: 5_000,
    staleTime: 2_000,
    retry: 2,
  });

  const statsQuery = useQuery({
    queryKey: ["market-24h", symbol],
    queryFn: () => fetchBinance24h(symbol),
    enabled: !!symbol,
    refetchInterval: 15_000,
    staleTime: 10_000,
    retry: 1,
  });

  return {
    history: historyQuery.data || [],
    isLoadingHistory: historyQuery.isLoading,
    historyError: historyQuery.error,

    currentPrice: priceQuery.data?.price ?? null,
    priceData: priceQuery.data || {},
    isLoadingPrice: priceQuery.isLoading,
    priceError: priceQuery.error,

    changePercent: statsQuery.data?.changePercent ?? null,
    statsData: statsQuery.data || {},
    isLoadingStats: statsQuery.isLoading,
    statsError: statsQuery.error,

    isLoading: historyQuery.isLoading || priceQuery.isLoading || statsQuery.isLoading,
    error: historyQuery.error || priceQuery.error || statsQuery.error,

    refetchHistory: historyQuery.refetch,
    refetchPrice: priceQuery.refetch,
    refetchStats: statsQuery.refetch,
  };
}

/**
 * Hook: useMarketPrices (list of markets)
 * Used by MarketsPage to show live prices for rows.
 *
 * Behavior:
 * - Try Binance 24h ticker for each symbol.
 * - If Binance fails AND symbol looks like FX (XXXUSDT), fallback to exchangerate.host.
 * - Always returns an object map: { [SYMBOL]: { price, changePercent, source? } }
 */
export function useMarketPrices(markets = [], opts = {}) {
  const refreshMs = Number(opts.refreshMs || 15000);

  const symbols = (Array.isArray(markets) ? markets : [])
    .map((m) => normalizeSymbol(m?.symbol || m?.id))
    .filter(Boolean);

  const key = symbols.join(",");

  const q = useQuery({
    queryKey: ["market-prices", key],
    enabled: symbols.length > 0,
    refetchInterval: refreshMs,
    staleTime: Math.max(2_000, Math.floor(refreshMs * 0.8)),
    retry: 1,
    queryFn: async () => {
      const out = {};

      // We fetch sequentially to keep it simple and avoid rate spikes.
      // If you want, we can batch/parallel with Promise.allSettled later.
      for (const sym of symbols) {
        try {
          const b = await fetchBinance24h(sym);
          out[b.symbol] = { price: b.price, changePercent: b.changePercent, source: "binance" };
        } catch (e) {
          // FX fallback if it looks like XXXUSDT
          try {
            const fx = await fetchFxFallback(sym);
            out[fx.symbol] = {
              price: fx.price,
              changePercent: fx.changePercent,
              source: fx.source,
            };
          } catch {
            // leave missing -> UI shows —
          }
        }
      }

      return out;
    },
  });

  const prices = q.data && typeof q.data === "object" ? q.data : {};

  const status = (() => {
    if (!symbols.length) return { ok: true, msg: "No markets" };
    if (q.isLoading) return { ok: true, msg: "Loading…" };
    if (q.isError) return { ok: false, msg: q.error?.message || "Price feed error" };
    return { ok: true, msg: "" };
  })();

  return { prices, status };
}

export default useMarketData;
